"use client";

import React, { useCallback, useEffect, useId, useRef } from 'react';

export type TabItem = {
  id: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  /** Trailing count or status. */
  badge?: React.ReactNode;
  disabled?: boolean;
};

export type TabsProps = {
  items: TabItem[];
  activeId: string;
  onChange: (id: string) => void;
  /** Announced to assistive technology as the purpose of the tab set. */
  label: string;
  className?: string;
};

/**
 * Page tabs, per the navigation patterns in app/design-system/catalog.ts:
 * "switch sibling views without changing the core entity".
 *
 * Implements the roving tabindex the ARIA tabs pattern requires — only the active
 * tab is in the tab order, and arrow keys move between them. Hand-rolled tab strips
 * across the app leave every tab tabbable, which makes a ten-tab strip ten stops on
 * the way to the content.
 *
 * Panels are the caller's responsibility; give the active panel
 * `id={panelId(activeId)}` and `aria-labelledby={tabId(activeId)}`.
 */
export function Tabs({ items, activeId, onChange, label, className = '' }: TabsProps) {
  const baseId = useId();
  const listRef = useRef<HTMLDivElement>(null);

  const tabId = (id: string) => `${baseId}-tab-${id}`;

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const list = listRef.current;
      const activeTab = list?.querySelector<HTMLButtonElement>(`#${CSS.escape(tabId(activeId))}`);
      if (list && activeTab && typeof list.scrollTo === 'function') {
        const centeredLeft = activeTab.offsetLeft - ((list.clientWidth - activeTab.offsetWidth) / 2);
        list.scrollTo({ left: Math.max(0, centeredLeft), behavior: 'auto' });
      }
    });
    return () => window.cancelAnimationFrame(frame);
  // The generated base id is stable for this mounted strip; active selection is
  // the only state that should reposition an overflowing tab list.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
      if (!keys.includes(event.key)) return;

      const enabled = items.filter((item) => !item.disabled);
      if (enabled.length === 0) return;
      const currentIndex = enabled.findIndex((item) => item.id === activeId);

      let nextIndex: number;
      if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = enabled.length - 1;
      else if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % enabled.length;
      else nextIndex = (currentIndex - 1 + enabled.length) % enabled.length;

      const next = enabled[nextIndex];
      if (!next) return;
      event.preventDefault();
      onChange(next.id);
      listRef.current?.querySelector<HTMLButtonElement>(`#${CSS.escape(tabId(next.id))}`)?.focus();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeId, items, onChange, baseId],
  );

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={label}
      onKeyDown={handleKeyDown}
      className={`flex items-center gap-1 overflow-x-auto border-b border-line ${className}`}
    >
      {items.map((item) => {
        const active = item.id === activeId;
        return (
          <button
            key={item.id}
            id={tabId(item.id)}
            role="tab"
            type="button"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            disabled={item.disabled}
            onClick={() => onChange(item.id)}
            className={`inline-flex min-h-12 shrink-0 items-center gap-2 border-b-2 px-3 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-canvas disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transition-none md:min-h-10 ${
              active
                ? 'border-brand text-content'
                : 'border-transparent text-content-muted hover:text-content'
            }`}
          >
            {item.icon ? <span aria-hidden="true">{item.icon}</span> : null}
            {item.label}
            {item.badge ? <span className="text-content-muted">{item.badge}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

export type SegmentedControlProps<T extends string> = {
  options: Array<{ value: T; label: React.ReactNode; icon?: React.ReactNode }>;
  value: T;
  onChange: (value: T) => void;
  label: string;
  size?: 'sm' | 'md';
  className?: string;
};

/**
 * Local presentation switch. The catalog caps this at "two or three mutually
 * exclusive choices" — past that it is page tabs or a select, not a segmented
 * control, because the segments get too narrow to label honestly.
 *
 * A radiogroup rather than tabs: it changes how the current view is drawn, not
 * which view is shown.
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  label,
  size = 'md',
  className = '',
}: SegmentedControlProps<T>) {
  const sizeClasses = size === 'sm'
    ? 'min-h-12 px-2.5 text-xs md:min-h-9'
    : 'min-h-12 px-3 text-sm md:min-h-9';

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={`inline-flex items-center gap-0.5 rounded-control border border-line bg-surface-subtle p-0.5 ${className}`}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            role="radio"
            type="button"
            aria-checked={active}
            onClick={() => onChange(option.value)}
            className={`inline-flex shrink-0 items-center justify-center gap-1.5 rounded-control font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-1 focus-visible:ring-offset-canvas motion-reduce:transition-none ${sizeClasses} ${
              active ? 'bg-surface text-content shadow-e1' : 'text-content-muted hover:text-content'
            }`}
          >
            {option.icon ? <span aria-hidden="true">{option.icon}</span> : null}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export default Tabs;
